import React, { useState } from 'react';
import { useTranslate, useRefresh } from 'react-admin';
import { SvgIcon } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import { EditButton, TextInput } from '~/components';
import { useNotify } from '~/hooks';
import SelectInput from './SelectInput';
import Confirmation from './Confirmation';
import { Grid } from '@material-ui/core';
import { useUpdateIndividualRiskRating } from '~/hooks/individuals';
import { useUpdateBusinessRiskRating } from '~/hooks/businesses';
import { updateEventLog } from '~/ducks/eventLog';
import { useDispatch } from 'react-redux';

export const label = (key: string): string => `components.UpdateRiskRating.${key}`;

interface Props {
  profileId: string;
  profileType: 'INDIVIDUAL' | 'BUSINESS';
}

const UpdateRiskRating = (props: Props) => {
  const { profileId, profileType } = props;

  const [riskRating, setRiskRating] = useState<string>();
  const [note, setNote] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<{ riskRating?: string; note?: string }>({});
  const handleNote = ({ target }: any) => {
    setErrorMessage((prev) => ({ ...prev, note: undefined }));
    setNote(target?.value);
  };
  const handleRiskRating = ({ target }: any) => {
    setErrorMessage((prev) => ({ ...prev, riskRating: undefined }));
    setRiskRating(target?.value);
  };

  const [, setOpen] = useState(false);
  const openDialog = () => setOpen(true);

  const translate = useTranslate();
  const t = (key: string): string => translate(label(key));

  const notify = useNotify();
  const dispatch = useDispatch();
  const forceRefresh = useRefresh();

  const updateIndividualMutation = useUpdateIndividualRiskRating(profileId);
  const updateBusinessMutation = useUpdateBusinessRiskRating(profileId);

  const handleProfileMutation =
    profileType === 'INDIVIDUAL' ? updateIndividualMutation : updateBusinessMutation;

  const onConfirm = async () => {
    let errors: { riskRating?: string; note?: string } = {};
    if (!note || !riskRating) {
      if (!riskRating) errors.riskRating = translate('ra.validation.required');
      if (!note) errors.note = translate('ra.validation.required');
      setErrorMessage(errors);
      throw new Error();
    } else if (note && riskRating) {
      await handleProfileMutation.mutateAsync({ riskRating, note });
      forceRefresh();
      dispatch(updateEventLog());
      notify({ type: 'success', message: t('profileRiskRatingUpdated') });
    }
  };

  return (
    <Confirmation
      onConfirm={onConfirm}
      onClose={() => {
        setRiskRating(undefined);
        setNote(undefined);
        setErrorMessage({});
      }}
      confirmButtonProps={{
        loading: handleProfileMutation.isLoading,
      }}
      confirmationSettings={{
        variant: 'modal',
        modal: {
          heading: t('updateRiskRating'),
          content: (
            <Grid container spacing={2} style={{ minWidth: 400 }}>
              <Grid item xs={12}>
                <SelectInput
                  label={t('riskRating')}
                  value={riskRating}
                  onChange={handleRiskRating}
                  options={[
                    { value: 'LOW', text: t('LOW') },
                    { value: 'MEDIUM', text: t('MEDIUM') },
                    { value: 'HIGH', text: t('HIGH') },
                  ]}
                  fullWidth
                  name='riskRating'
                  error={!!errorMessage.riskRating}
                  helperText={errorMessage.riskRating}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextInput
                  label={t('note')}
                  value={note}
                  onChange={handleNote}
                  fullWidth
                  multiline
                  error={!!errorMessage.note}
                  helperText={errorMessage.note}
                  rows={4}
                  required
                />
              </Grid>
            </Grid>
          ),
        },
      }}
    >
      <EditButton
        label={t('updateRiskRating')}
        icon={
          <SvgIcon>
            <WarningIcon />
          </SvgIcon>
        }
        onClick={openDialog}
        red
      />
    </Confirmation>
  );
};

export default UpdateRiskRating;
